// src/components/education/periodic-table/MaterialsExplorer.tsx
// A component that displays the Materials Explorer with different material categories
import React, { useState } from 'react';
import styled from 'styled-components';
import MaterialCard from './MaterialCard';
import MaterialDetail from './MaterialDetail';
// Import images directly
import ConcreteImg from '../../../assets/images/PeriodicTableMaterials/Concrete.webp';
import SteelImg from '../../../assets/images/PeriodicTableMaterials/Steel.webp';
import WoodImg from '../../../assets/images/PeriodicTableMaterials/Wood.webp';
import AluminumImg from '../../../assets/images/PeriodicTableMaterials/Aluminum.webp';
import CopperImg from '../../../assets/images/PeriodicTableMaterials/Copper.webp';
import MasonryImg from '../../../assets/images/PeriodicTableMaterials/Masonry.webp';
import DrywallImg from '../../../assets/images/PeriodicTableMaterials/Drywall.webp';
import InsulationImg from '../../../assets/images/PeriodicTableMaterials/Insulation.webp';
import PaintImg from '../../../assets/images/PeriodicTableMaterials/Paint.webp';
import VinylFlooringImg from '../../../assets/images/PeriodicTableMaterials/VinylFlooring.webp';
import TileImg from '../../../assets/images/PeriodicTableMaterials/Tile.webp';
import ConcreteAdmixtureImg from '../../../assets/images/PeriodicTableMaterials/ConcreteAdmixture.webp';
import SolventImg from '../../../assets/images/PeriodicTableMaterials/Solvent.webp';
import AdhesiveImg from '../../../assets/images/PeriodicTableMaterials/Adhesive.webp';
import TapLogo from '../../../assets/images/TAP-Logo.webp';

// Define materials data interface
interface Material {
  id: string;
  name: string;
  description: string;
  image: string;
  category: 'structural' | 'metal' | 'interior' | 'specialty';
  detailTitle: string;
  detailDescription: string;
  keyComponents: { element: string; description: string }[];
  applications: string[];
}

// Materials data
const materials: Material[] = [
  {
    id: 'concrete',
    name: 'Concrete',
    description: 'Versatile construction material',
    image: ConcreteImg,
    category: 'structural',
    detailTitle: 'Concrete: The Foundation of Modern Construction',
    detailDescription: 'Concrete is one of the most widely used construction materials worldwide, valued for its durability, strength, and impressive strength. As a mix-in, concrete begins as a flowing slurry that hardens into a stone-like substance. The key to concrete\'s success is its versatility and adaptability.',
    keyComponents: [
      { element: 'Calcium Oxide (CaO)', description: 'Primary element in cement (as calcium silicates and aluminates)' },
      { element: 'Silicon Dioxide (SiO₂)', description: 'Major component in cement and aggregate' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Contributes to strength and durability properties' },
      { element: 'Water (H₂O)', description: 'Needed to initiate process of cement' },
      { element: 'Iron Oxide (Fe₂O₃)', description: 'Present in cement minerals and improves workability' }
    ],
    applications: ['Infrastructure (highways, bridges)', 'Buildings (skyscrapers, homes)', 'Dams, tunnels, and airports']
  },
  {
    id: 'steel',
    name: 'Steel',
    description: 'Strong, versatile structural metal',
    image: SteelImg,
    category: 'structural',
    detailTitle: 'Steel: The Backbone of Modern Infrastructure',
    detailDescription: 'Steel is an alloy of iron and carbon, and sometimes other elements like manganese and chromium. Its high tensile strength and low cost make it a backbone of industrial applications, construction, weapons, and transportation.',
    keyComponents: [
      { element: 'Iron (Fe)', description: 'Primary element (98-99%)' },
      { element: 'Carbon (C)', description: 'Crucial alloying element (0.03-2.14%)' },
      { element: 'Manganese (Mn)', description: 'Improves hardenability and prevents brittleness' },
      { element: 'Silicon (Si)', description: 'Deoxidizes and improves strength' },
      { element: 'Chromium (Cr)', description: 'For corrosion resistance in stainless steels' }
    ],
    applications: ['Construction (buildings, bridges)', 'Transportation (vehicles, ships)', 'Energy sector', 'Infrastructure']
  },
  {
    id: 'wood',
    name: 'Wood',
    description: 'Natural structural material',
    image: WoodImg,
    category: 'structural',
    detailTitle: 'Wood: Nature\'s Versatile Building Material',
    detailDescription: 'Wood has been used for thousands of years as a construction material due to its availability, renewable nature, and excellent strength-to-weight ratio. It remains one of the most sustainable building materials.',
    keyComponents: [
      { element: 'Cellulose (C₆H₁₀O₅)n', description: 'Main structural component (40-50%)' },
      { element: 'Lignin', description: 'Acts as a natural binder, giving wood its rigidity' },
      { element: 'Hemicellulose', description: 'Supports cellulose and lignin structure' },
      { element: 'Carbon (C)', description: 'Primary element in organic wood structure' },
      { element: 'Oxygen (O)', description: 'Essential component in wood chemistry' }
    ],
    applications: ['Residential construction', 'Furniture', 'Interior design', 'Decorative elements']
  },
  {
    id: 'aluminum',
    name: 'Aluminum',
    description: 'Lightweight, corrosion-resistant',
    image: AluminumImg,
    category: 'metal',
    detailTitle: 'Aluminum: Lightweight Champion of Metals',
    detailDescription: 'Aluminum is prized for its light weight, corrosion resistance, and excellent thermal and electrical conductivity. It\'s the most abundant metal in Earth\'s crust and has revolutionized many industries.',
    keyComponents: [
      { element: 'Aluminum (Al)', description: 'Primary element (typically 90-99%)' },
      { element: 'Silicon (Si)', description: 'Common alloying element for casting' },
      { element: 'Magnesium (Mg)', description: 'Increases strength in aluminum alloys' },
      { element: 'Copper (Cu)', description: 'Improves strength in some alloys' },
      { element: 'Zinc (Zn)', description: 'Enhances corrosion resistance and strength' }
    ],
    applications: ['Aerospace', 'Transportation', 'Building construction', 'Packaging (foils, cans)']
  },
  {
    id: 'copper',
    name: 'Copper',
    description: 'Excellent electrical conductor',
    image: CopperImg,
    category: 'metal',
    detailTitle: 'Copper: The Essential Electrical Conductor',
    detailDescription: 'Copper has been used for over 10,000 years and is essential for modern electrical applications due to its excellent conductivity. It also exhibits antimicrobial properties and forms various useful alloys.',
    keyComponents: [
      { element: 'Copper (Cu)', description: 'Primary element (typically 99.9% in electrical applications)' },
      { element: 'Zinc (Zn)', description: 'Added to form brass' },
      { element: 'Tin (Sn)', description: 'Added to form bronze' },
      { element: 'Nickel (Ni)', description: 'Used in certain specialized alloys' },
      { element: 'Silver (Ag)', description: 'Sometimes present in trace amounts' }
    ],
    applications: ['Electrical wiring', 'Plumbing', 'Electronics', 'Architecture (roofing, cladding)']
  },
  {
    id: 'masonry',
    name: 'Masonry',
    description: 'Stone, brick, or concrete blocks',
    image: MasonryImg,
    category: 'metal',
    detailTitle: 'Masonry: Ancient Building Technique for Modern Structures',
    detailDescription: 'Masonry construction uses individual units such as stones, bricks, or concrete blocks laid and bound together by mortar. It is one of the oldest construction methods and remains vital to modern building practices.',
    keyComponents: [
      { element: 'Silicon Dioxide (SiO₂)', description: 'Major component in clay bricks and stone' },
      { element: 'Calcium Oxide (CaO)', description: 'Key element in mortar binding' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Present in clay and cement components' },
      { element: 'Iron Oxide (Fe₂O₃)', description: 'Gives red color to many bricks' },
      { element: 'Magnesium Oxide (MgO)', description: 'Found in some natural stones used in masonry' }
    ],
    applications: ['Load-bearing walls', 'Façades', 'Foundations', 'Decorative elements']
  },
  {
    id: 'drywall',
    name: 'Drywall',
    description: 'Paneling with gypsum core',
    image: DrywallImg,
    category: 'interior',
    detailTitle: 'Drywall: The Standard for Modern Interior Walls',
    detailDescription: 'Drywall (also known as plasterboard or gypsum board) consists of a layer of gypsum plaster pressed between two thick sheets of paper. It is widely used to create interior walls and ceilings in modern construction.',
    keyComponents: [
      { element: 'Calcium Sulfate Dihydrate (CaSO₄·2H₂O)', description: 'Gypsum, the main component (about 90%)' },
      { element: 'Silicon Dioxide (SiO₂)', description: 'Present in the paper covering' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Found in additives for fire resistance' },
      { element: 'Magnesium Oxide (MgO)', description: 'Added to some moisture-resistant variants' },
      { element: 'Cellulose (C₆H₁₀O₅)n', description: 'Main component in the paper facing' }
    ],
    applications: ['Interior walls', 'Ceilings', 'Room partitions', 'Sound insulation']
  },
  {
    id: 'insulation',
    name: 'Insulation',
    description: 'Materials reducing heat transfer',
    image: InsulationImg,
    category: 'interior',
    detailTitle: 'Insulation: Energy Efficiency Through Material Science',
    detailDescription: 'Insulation materials are designed to reduce heat transfer between interior and exterior environments, improving energy efficiency and comfort in buildings. They come in various forms including fiberglass, foam, cellulose, and mineral wool.',
    keyComponents: [
      { element: 'Silicon Dioxide (SiO₂)', description: 'Main component in fiberglass insulation' },
      { element: 'Carbon (C)', description: 'Primary element in foam insulation' },
      { element: 'Aluminum (Al)', description: 'Used in reflective insulation barriers' },
      { element: 'Nitrogen (N)', description: 'Component in foam insulation blowing agents' },
      { element: 'Boron (B)', description: 'Used as flame retardant in some insulations' }
    ],
    applications: ['Thermal insulation', 'Acoustic dampening', 'Moisture control', 'Fire protection']
  },
  {
    id: 'paint',
    name: 'Paints',
    description: 'Decorative and protective',
    image: PaintImg,
    category: 'interior',
    detailTitle: 'Paints: Protection and Aesthetics for Surfaces',
    detailDescription: 'Paint is a liquid mixture that converts to a solid film after application to a substrate. Modern paints consist of pigments, binders, solvents, and additives, providing both decorative and protective functions for surfaces.',
    keyComponents: [
      { element: 'Titanium Dioxide (TiO₂)', description: 'White pigment and opacity provider' },
      { element: 'Carbon (C)', description: 'Present in organic polymers and pigments' },
      { element: 'Iron Oxide (Fe₂O₃)', description: 'Used for red, brown, and yellow pigments' },
      { element: 'Silicon Dioxide (SiO₂)', description: 'Present in fillers and extenders' },
      { element: 'Zinc Oxide (ZnO)', description: 'Used as white pigment and mildew inhibitor' }
    ],
    applications: ['Interior decoration', 'Exterior protection', 'Waterproofing', 'Mold prevention']
  },
  {
    id: 'flooring',
    name: 'Vinyl Flooring',
    description: 'Durable synthetic floor covering',
    image: VinylFlooringImg,
    category: 'interior',
    detailTitle: 'Vinyl Flooring: Versatile and Durable Surface Solution',
    detailDescription: 'Vinyl flooring is a synthetic material made primarily from polyvinyl chloride (PVC) with added plasticizers, stabilizers, and pigments. It offers water resistance, durability, and comes in a wide variety of styles that can mimic natural materials.',
    keyComponents: [
      { element: 'Chlorine (Cl)', description: 'Key component in polyvinyl chloride (PVC)' },
      { element: 'Carbon (C)', description: 'Main element in vinyl polymers' },
      { element: 'Hydrogen (H)', description: 'Present in the molecular structure of PVC' },
      { element: 'Calcium Carbonate (CaCO₃)', description: 'Common filler material' },
      { element: 'Titanium Dioxide (TiO₂)', description: 'Used for white coloration and opacity' }
    ],
    applications: ['Residential flooring', 'Commercial spaces', 'Healthcare facilities', 'Retail environments']
  },
  {
    id: 'tile',
    name: 'Tile',
    description: 'Durable decorative surface material',
    image: TileImg,
    category: 'interior',
    detailTitle: 'Tile: Durable and Decorative Surface Material',
    detailDescription: 'Ceramic and porcelain tiles are made from natural clay materials fired at high temperatures. They offer exceptional durability, water resistance, and versatility in design, making them ideal for various applications in construction.',
    keyComponents: [
      { element: 'Silicon Dioxide (SiO₂)', description: 'Primary component in clay and silica' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Present in clay materials' },
      { element: 'Calcium Oxide (CaO)', description: 'Component in some glazes' },
      { element: 'Sodium Oxide (Na₂O)', description: 'Used in glazes for ceramic tiles' },
      { element: 'Potassium Oxide (K₂O)', description: 'Present in feldspars used in porcelain' }
    ],
    applications: ['Floor coverings', 'Wall surfaces', 'Bathrooms and kitchens', 'Decorative accents']
  },
  {
    id: 'admixtures',
    name: 'Concrete Admixtures',
    description: 'Enhancing concrete properties',
    image: ConcreteAdmixtureImg,
    category: 'specialty',
    detailTitle: 'Concrete Admixtures: Enhancing Performance and Workability',
    detailDescription: 'Concrete admixtures are materials added to concrete mixtures to modify their properties. They can improve workability, setting time, strength, durability, and other characteristics, allowing concrete to be tailored for specific applications.',
    keyComponents: [
      { element: 'Calcium Chloride (CaCl₂)', description: 'Accelerates setting and hardening' },
      { element: 'Sodium Gluconate (C₆H₁₁NaO₇)', description: 'Retards setting for extended workability' },
      { element: 'Calcium Lignosulfonate', description: 'Water-reducing agent' },
      { element: 'Silicon Dioxide (SiO₂)', description: 'Pozzolanic material for strength' },
      { element: 'Aluminum Powder (Al)', description: 'Gas-forming agent in cellular concrete' }
    ],
    applications: ['Accelerating or retarding set time', 'Improving workability', 'Enhancing strength', 'Controlling shrinkage']
  },
  {
    id: 'solvent',
    name: 'Solvents',
    description: 'Dissolving agents for coatings',
    image: SolventImg,
    category: 'specialty',
    detailTitle: 'Solvents: Essential Dissolving Agents in Construction',
    detailDescription: 'Solvents are liquid substances that dissolve other materials to form solutions. In construction, they are vital components of paints, coatings, adhesives, and cleaning agents, facilitating application and affecting drying times.',
    keyComponents: [
      { element: 'Carbon (C)', description: 'Principal element in organic solvents' },
      { element: 'Hydrogen (H)', description: 'Present in all organic solvents' },
      { element: 'Oxygen (O)', description: 'Found in alcohols, ketones, and esters' },
      { element: 'Nitrogen (N)', description: 'Present in some specialized solvents' },
      { element: 'Chlorine (Cl)', description: 'Component in chlorinated solvents' }
    ],
    applications: ['Paint thinners', 'Adhesive carriers', 'Cleaning agents', 'Surface preparation']
  },
  {
    id: 'adhesives',
    name: 'Adhesives',
    description: 'Bonding construction materials',
    image: AdhesiveImg,
    category: 'specialty',
    detailTitle: 'Adhesives: Creating Strong Bonds in Construction',
    detailDescription: 'Adhesives are substances capable of holding materials together by surface attachment. Modern construction adhesives range from natural glues to sophisticated synthetic polymers designed for specific applications and substrates.',
    keyComponents: [
      { element: 'Carbon (C)', description: 'Main element in polymer-based adhesives' },
      { element: 'Silicon (Si)', description: 'Present in silicone adhesives' },
      { element: 'Nitrogen (N)', description: 'Component in polyurethane and epoxy adhesives' },
      { element: 'Oxygen (O)', description: 'Present in functional groups of adhesive polymers' },
      { element: 'Hydrogen (H)', description: 'Part of organic structure and hydroxyl groups' }
    ],
    applications: ['Structural bonding', 'Flooring installation', 'Paneling and trim', 'Woodworking']
  },
  {
    id: 'fiber-cement',
    name: 'Fiber Cement',
    description: 'Durable exterior cladding material',
    image: TapLogo,
    category: 'structural',
    detailTitle: 'Fiber Cement: Modern Exterior Cladding Solution',
    detailDescription: 'Fiber cement is a composite material made of cement reinforced with cellulose fibers. It offers excellent durability, fire resistance, and low maintenance requirements, making it ideal for exterior cladding in multifamily construction.',
    keyComponents: [
      { element: 'Cement (CaO)', description: 'Primary binding material' },
      { element: 'Cellulose Fibers', description: 'Provides reinforcement and flexibility' },
      { element: 'Sand (SiO₂)', description: 'Adds strength and texture' },
      { element: 'Water (H₂O)', description: 'For hydration and curing' },
      { element: 'Additives', description: 'Improve workability and durability' }
    ],
    applications: ['Exterior siding', 'Fascia boards', 'Trim elements', 'Soffit panels']
  },
  {
    id: 'gypsum-board',
    name: 'Gypsum Board',
    description: 'Standard interior wall and ceiling material',
    image: TapLogo,
    category: 'interior',
    detailTitle: 'Gypsum Board: The Standard for Interior Walls',
    detailDescription: 'Gypsum board, commonly known as drywall, is a panel made of gypsum plaster pressed between two thick sheets of paper. It is the most common material used for interior walls and ceilings in modern construction.',
    keyComponents: [
      { element: 'Gypsum (CaSO₄·2H₂O)', description: 'Core material providing fire resistance' },
      { element: 'Paper', description: 'Outer layers for strength and finish' },
      { element: 'Additives', description: 'Improve fire resistance and moisture resistance' }
    ],
    applications: ['Interior walls', 'Ceilings', 'Partitions', 'Fire-rated assemblies']
  },
  {
    id: 'epdm',
    name: 'EPDM',
    description: 'Durable roofing membrane',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'EPDM: Reliable Roofing Membrane',
    detailDescription: 'Ethylene Propylene Diene Monomer (EPDM) is a synthetic rubber roofing membrane known for its durability, weather resistance, and long lifespan. It is commonly used in low-slope roofing applications.',
    keyComponents: [
      { element: 'Ethylene (C₂H₄)', description: 'Primary monomer (45-75%)' },
      { element: 'Propylene (C₃H₆)', description: 'Co-monomer (20-40%)' },
      { element: 'Diene (C₅H₈)', description: 'Cross-linking agent (2-5%)' },
      { element: 'Carbon Black (C)', description: 'UV protection and reinforcement (20-30%)' },
      { element: 'Zinc Oxide (ZnO)', description: 'Activator for vulcanization (2-5%)' },
      { element: 'Sulfur (S)', description: 'Vulcanizing agent (1-2%)' },
      { element: 'Antioxidants', description: 'Prevent degradation (1-2%)' }
    ],
    applications: ['Low-slope roofing', 'Pond liners', 'Waterproofing membranes', 'HVAC duct insulation']
  },
  {
    id: 'pvc-pipe',
    name: 'PVC Pipe',
    description: 'Versatile plumbing material',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'PVC Pipe: Essential Plumbing Material',
    detailDescription: 'Polyvinyl Chloride (PVC) pipe is a widely used material in plumbing systems due to its durability, chemical resistance, and ease of installation. It is commonly used for drain, waste, and vent systems.',
    keyComponents: [
      { element: 'Vinyl Chloride (C₂H₃Cl)', description: 'Primary polymer component' },
      { element: 'Carbon (C)', description: 'Main element in polymer backbone' },
      { element: 'Hydrogen (H)', description: 'Part of vinyl chloride structure' },
      { element: 'Chlorine (Cl)', description: 'Provides chemical resistance' },
      { element: 'Calcium Carbonate (CaCO₃)', description: 'Common filler material' },
      { element: 'Titanium Dioxide (TiO₂)', description: 'UV stabilizer and pigment' },
      { element: 'Lead (Pb)', description: 'Stabilizer in some formulations' },
      { element: 'Tin (Sn)', description: 'Alternative stabilizer' }
    ],
    applications: ['Drain systems', 'Waste systems', 'Vent systems', 'Underground utilities']
  },
  {
    id: 'mineral-wool',
    name: 'Mineral Wool',
    description: 'Fire-resistant insulation material',
    image: TapLogo,
    category: 'interior',
    detailTitle: 'Mineral Wool: Superior Fire Protection',
    detailDescription: 'Mineral wool is an insulation material made from molten rock or slag that is spun into fibers. It provides excellent fire resistance, sound absorption, and thermal insulation properties.',
    keyComponents: [
      { element: 'Silicon Dioxide (SiO₂)', description: 'Primary component (40-50%)' },
      { element: 'Calcium Oxide (CaO)', description: 'Stabilizing agent (15-25%)' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Improves chemical resistance (10-15%)' },
      { element: 'Magnesium Oxide (MgO)', description: 'Enhances durability (5-15%)' },
      { element: 'Iron Oxide (Fe₂O₃)', description: 'Present in raw materials (5-10%)' },
      { element: 'Sodium Oxide (Na₂O)', description: 'Fluxing agent (0-5%)' },
      { element: 'Potassium Oxide (K₂O)', description: 'Fluxing agent (0-5%)' }
    ],
    applications: ['Fire-rated assemblies', 'Sound insulation', 'Thermal insulation', 'Fire stopping']
  },
  {
    id: 'asphalt-shingles',
    name: 'Asphalt Shingles',
    description: 'Most common residential roofing material',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'Asphalt Shingles: The Standard in Residential Roofing',
    detailDescription: 'Asphalt shingles are the most common roofing material in North America, known for their affordability, durability, and ease of installation. They consist of a fiberglass or organic mat coated with asphalt and mineral granules.',
    keyComponents: [
      { element: 'Asphalt (C₅H₈)', description: 'Bituminous waterproofing component' },
      { element: 'Fiberglass (SiO₂)', description: 'Reinforcement mat material (45-55%)' },
      { element: 'Mineral Granules (SiO₂, Al₂O₃)', description: 'UV protection and aesthetic finish' },
      { element: 'Limestone (CaCO₃)', description: 'Filler material in asphalt' },
      { element: 'Sand (SiO₂)', description: 'Additional filler material' }
    ],
    applications: ['Residential roofing', 'Low-slope commercial roofs', 'Sheds and outbuildings']
  },
  {
    id: 'glass-fiber',
    name: 'Glass Fiber',
    description: 'Reinforcement material for composites',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'Glass Fiber: The Backbone of Modern Composites',
    detailDescription: 'Glass fiber is a material consisting of numerous extremely fine fibers of glass. It is used as a reinforcing agent for many polymer products, creating strong, lightweight materials known as fiberglass.',
    keyComponents: [
      { element: 'Silicon Dioxide (SiO₂)', description: 'Primary component (52-56%)' },
      { element: 'Calcium Oxide (CaO)', description: 'Stabilizing agent (16-25%)' },
      { element: 'Aluminum Oxide (Al₂O₃)', description: 'Improves chemical resistance (12-16%)' },
      { element: 'Boron Oxide (B₂O₃)', description: 'Lowers melting temperature (8-13%)' },
      { element: 'Magnesium Oxide (MgO)', description: 'Improves durability (0-5%)' }
    ],
    applications: ['Fiberglass insulation', 'Composite materials', 'Reinforcement for concrete', 'Thermal insulation']
  },
  {
    id: 'acm',
    name: 'ACM Panels',
    description: 'Architectural composite material',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'ACM Panels: Modern Architectural Cladding',
    detailDescription: 'Aluminum Composite Material (ACM) panels consist of two thin aluminum sheets bonded to a non-aluminum core. They are widely used in modern architecture for exterior cladding due to their lightweight, durability, and aesthetic flexibility.',
    keyComponents: [
      { element: 'Aluminum (Al)', description: 'Outer layers (0.2-0.5mm each)' },
      { element: 'Polyethylene (C₂H₄)n', description: 'Core material (3-6mm)' },
      { element: 'Adhesive (C₉H₁₀O₃)', description: 'Bonds layers together' },
      { element: 'Chromium (Cr)', description: 'Surface treatment for corrosion resistance' },
      { element: 'Zinc (Zn)', description: 'Coating for additional protection' }
    ],
    applications: ['Exterior cladding', 'Signage', 'Interior wall panels', 'Architectural features']
  },
  {
    id: 'geotextile',
    name: 'Geotextile',
    description: 'Permeable fabric for soil stabilization',
    image: TapLogo,
    category: 'specialty',
    detailTitle: 'Geotextile: Engineering the Ground',
    detailDescription: 'Geotextiles are permeable fabrics used in construction to improve soil characteristics. They provide separation, filtration, reinforcement, protection, and drainage functions in civil engineering applications.',
    keyComponents: [
      { element: 'Polypropylene (C₃H₆)n', description: 'Most common polymer used (90-95%)' },
      { element: 'Polyester (C₁₀H₈O₄)n', description: 'Alternative polymer with higher strength' },
      { element: 'Carbon Black (C)', description: 'UV stabilizer and pigment' },
      { element: 'Antioxidants (C₁₅H₂₄O₄)', description: 'Prevent degradation' },
      { element: 'Lubricants (C₁₈H₃₈)', description: 'Improve processing' }
    ],
    applications: ['Soil stabilization', 'Erosion control', 'Drainage systems', 'Road construction']
  }
];

// Styled components
const Container = styled.div`
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  width: 350px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-height: 600px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(149, 117, 205, 0.5);
    border-radius: 4px;
  }
`;

const Title = styled.h2`
  color: #9575CD;
  font-size: 1.4rem;
  text-align: center;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const CategoryTitle = styled.h3`
  color: #9575CD;
  font-size: 1.2rem;
  margin: 20px 0 15px 0;
  border-left: 3px solid #9575CD;
  padding-left: 10px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

const ViewMore = styled.div`
  color: rgba(149, 117, 205, 0.8);
  font-size: 0.9rem;
  text-align: right;
  margin: 10px 15px 20px 0;
  cursor: pointer;
  font-weight: bold;
  padding: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #9575CD;
    text-decoration: underline;
    transform: translateX(-5px);
  }
`;

interface MaterialsExplorerProps {
  onMaterialSelect?: (elementSymbols: string[]) => void;
}

const MaterialsExplorer: React.FC<MaterialsExplorerProps> = ({ onMaterialSelect }) => {
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  
  // Extract element symbols from a material's key components
  const extractElementSymbols = (material: Material): string[] => {
    const symbolMap = new Map<string, boolean>();
    
    material.keyComponents.forEach(comp => {
      // Handle different formats like "Calcium Oxide (CaO)", "Iron (Fe)", etc.
      // Extract element symbols from chemical formulas
      
      // First, try to extract the formula inside parentheses
      const match = comp.element.match(/\(([^)]+)\)/);
      if (match) {
        const formula = match[1];
        
        // Check for basic elements like (Fe), (Cu), etc.
        if (/^[A-Z][a-z]?$/.test(formula)) {
          symbolMap.set(formula, true);
        } 
        // Check for oxides and compounds like CaO, SiO₂, Al₂O₃, etc.
        else {
          // Extract element symbols from chemical formulas
          const elementMatches = formula.match(/[A-Z][a-z]?/g);
          if (elementMatches) {
            elementMatches.forEach(symbol => {
              symbolMap.set(symbol, true);
            });
          }
        }
      } 
      // Check for elements without parentheses
      else {
        const elementName = comp.element.split(' ')[0]; // Take the first word (e.g., "Carbon" from "Carbon (C)")
        
        // Map common element names directly
        const commonElements: {[key: string]: string} = {
          'Carbon': 'C',
          'Hydrogen': 'H',
          'Oxygen': 'O',
          'Nitrogen': 'N',
          'Silicon': 'Si',
          'Calcium': 'Ca',
          'Iron': 'Fe',
          'Aluminum': 'Al',
          'Aluminium': 'Al',
          'Magnesium': 'Mg',
          'Sodium': 'Na',
          'Potassium': 'K',
          'Sulfur': 'S',
          'Chlorine': 'Cl',
          'Titanium': 'Ti',
          'Copper': 'Cu',
          'Zinc': 'Zn',
          'Lead': 'Pb',
          'Tin': 'Sn'
        };
        
        if (commonElements[elementName]) {
          symbolMap.set(commonElements[elementName], true);
        }
      }
    });
    
    return Array.from(symbolMap.keys());
  };
  
  const handleCardClick = (material: Material) => {
    setSelectedMaterial(material);
    
    // Get element symbols from the keyComponents
    if (onMaterialSelect) {
      const elementSymbols = extractElementSymbols(material);
      onMaterialSelect(elementSymbols);
    }
  };
  
  const handleCloseDetail = () => {
    setSelectedMaterial(null);
    // Clear element highlights when closing detail
    if (onMaterialSelect) {
      onMaterialSelect([]);
    }
  };
  
  // Handle hover on material card
  const handleCardMouseEnter = (material: Material) => {
    const elementSymbols = extractElementSymbols(material);
    if (onMaterialSelect) {
      onMaterialSelect(elementSymbols);
    }
  };
  
  // Handle mouse leave from material card
  const handleCardMouseLeave = () => {
    // Only clear highlights if no material is selected
    if (onMaterialSelect && !selectedMaterial) {
      onMaterialSelect([]);
    } else if (onMaterialSelect && selectedMaterial) {
      // If a material is selected, keep its elements highlighted
      onMaterialSelect(extractElementSymbols(selectedMaterial));
    }
  };
  
  // Group materials by category
  const structuralMaterials = materials.filter(m => m.category === 'structural');
  const metalMaterials = materials.filter(m => m.category === 'metal');
  const interiorMaterials = materials.filter(m => m.category === 'interior');
  const specialtyMaterials = materials.filter(m => m.category === 'specialty');
  
  // Create a material card with hover functionality
  const renderMaterialCard = (material: Material) => (
    <MaterialCard 
      key={material.id}
      name={material.name}
      description={material.description}
      image={material.image}
      onClick={() => handleCardClick(material)}
      onMouseEnter={() => handleCardMouseEnter(material)}
      onMouseLeave={handleCardMouseLeave}
    />
  );
  
  return (
    <>
      <Container>
        <Title>Materials Explorer</Title>
        
        <CategoryTitle>Structural Materials</CategoryTitle>
        <CardsContainer>
          {structuralMaterials.map(renderMaterialCard)}
        </CardsContainer>
        <ViewMore>VIEW MORE</ViewMore>
        
        <CategoryTitle>Metal Materials</CategoryTitle>
        <CardsContainer>
          {metalMaterials.map(renderMaterialCard)}
        </CardsContainer>
        <ViewMore>VIEW MORE</ViewMore>
        
        <CategoryTitle>Interior Materials</CategoryTitle>
        <CardsContainer>
          {interiorMaterials.map(renderMaterialCard)}
        </CardsContainer>
        <ViewMore>VIEW MORE</ViewMore>
        
        <CategoryTitle>Specialty Materials</CategoryTitle>
        <CardsContainer>
          {specialtyMaterials.map(renderMaterialCard)}
        </CardsContainer>
        <ViewMore>VIEW MORE</ViewMore>
      </Container>
      
      {selectedMaterial && (
        <MaterialDetail
          title={selectedMaterial.detailTitle}
          description={selectedMaterial.detailDescription}
          keyComponents={selectedMaterial.keyComponents}
          applications={selectedMaterial.applications}
          onClose={handleCloseDetail}
        />
      )}
    </>
  );
};

export default MaterialsExplorer; 